import * as d3 from 'd3';
import i18next from 'i18next';

/**
 * Generates a histogram chart within a specified container.
 *
 * @param {string} container_id - The ID of the container element where the histogram will be rendered.
 * @param {number[]} array - An array of numbers to generate the histogram from.
 * @param {number} input_width - The width of the histogram chart.
 *
 * @returns {SVGElement} The generated SVG element containing the histogram chart.
 */
export function gen_hist(container_id, array, input_width) {
    // count frequency
    const frequency = {};
    array.forEach(num => {
        if (!frequency[num]) {
            frequency[num] = 0;
        }
        frequency[num]++;
    });

    const histogramData = Object.keys(frequency).map(key => ({
        number: key,
        count: frequency[key]
    }));

    const margin = { top: 30, right: 30, bottom: 45, left: 40 };
    const width = input_width - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    const x = d3.scaleBand()
        .domain(histogramData.map(d => d.number))
        .range([0, width])
        .padding(0.1);

    const xAxis = d3.axisBottom(x).tickSizeOuter(0);

    const y = d3.scaleLinear()
        .domain([0, d3.max(histogramData, d => d.count)])
        .nice()
        .range([height, 0]);

    const tooltip = d3.select(`#${container_id}`)
        .append('div')
        .style('position', 'absolute')
        .style('font-size', '12px')
        .style('opacity', 0) // Initially hidden
        .style('background', 'rgba(0, 0, 0, 0.6)')
        .style('color', '#fff')
        .style('border-radius', '5px')
        .style('padding', '5px');

    let selected_bar = null;

    const svg = d3.select(`#${container_id}`)
        .append("svg")
        .attr("width", width + margin.left + margin.right)
        .attr("height", height + margin.top + margin.bottom)
        .style("position", "relative")
        .call(zoom)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`)
        .attr("style", "max-width: box_100%; height: auto;");

    svg.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(0,${height})`)
        .attr("color", "gray")
        .call(xAxis)
        .call(g => g.append("text")
            .attr("fill", "currentColor")
            .attr('x', margin.left + (width - margin.left - margin.right) / 2)
            .attr('y', 40)
            .attr("class", "card-sm-num text-center hist_x_title")
            .style("font-size", "12px")
            .text("")
        );

    svg.append("g")
        .attr("class", "y-axis")
        .attr("color", "gray")
        .call(d3.axisLeft(y))
        .call(g => g.select(".domain").remove())
        .call(g => g.append("text")
            .attr("x", -margin.left)
            .attr("y", -15)
            .attr("fill", "currentColor")
            .attr("text-anchor", "start")
            .attr("class", "card-sm-num text-center")
            .attr("data-i18n", "label-dataset-num")
            .style("font-size", "12px")
            .text(i18next.t('label-dataset-num'))
        );

    svg.append("g")
        .attr("class", "bars")
        .attr("fill", "url(#gradient)") 
        .selectAll("rect")
        .data(histogramData)
        .enter().append("rect")
        .attr("x", d => x(d.number))
        .attr("width", x.bandwidth())
        .attr("y", d => y(0))

        // Events
        .on("mouseover", function(event, d) {
            d3.select(this)
                .attr('fill', '#f3f1c1')
                .attr("stroke", "#fdab13")
                .attr("stroke-width", "2px");

            tooltip.style("opacity", 1)
        })
        .on("mousemove", function(event, d) {
            const rect = d3.select(this);
            const x = +rect.attr('x');
            const rect_width = +rect.attr('width');
            const y = +rect.attr('y');
            let top = Math.max(margin.top*4 + y, margin.top*4);

            tooltip.style("opacity", 1)
                .text(d.count)
                .style("left", `${margin.left*1.85 + x + rect_width/2}px`)
                .style("top", `${top}px`)
                .style("z-index", "100");
        })
        .on("mouseout", function(event, d) {
            if(selected_bar !== this) {
                d3.select(this)
                .attr('fill', 'url(#gradient)')
                .attr("stroke-width", "0px"); 
            }

            tooltip.style("opacity", 0);
        })
    
        //init animation
        .transition() // Apply transition for bar height
        .duration(1000) // Animation duration in milliseconds
        .attr("y", d => y(d.count)) // Transition to the actual y position based on data
        .attr("height", d => height -  y(d.count)) // Transition to the actual height based on data;

    return svg.node();

    function zoom(svg) {
        const extent = [[0, 0], [width, height]];

        svg.call(d3.zoom()
            .scaleExtent([1, 15])
            .translateExtent(extent)
            .extent(extent)
            .on("zoom", zoomed));

        function zoomed(event) {
            x.range([0, width].map(d => event.transform.applyX(d)));
            svg.selectAll(".bars rect").attr("x", d => x(d.number)).attr("width", x.bandwidth());
            svg.selectAll(".x-axis").call(xAxis)

            y.range([height, 0].map(d => event.transform.applyY(d)));
            svg.selectAll(".bars rect").attr("y", d => y(d.count)).attr("height", d => height - y(d.count));
            svg.selectAll(".y-axis").call(d3.axisLeft(y));
        }
    }
}

/**
 * Updates or creates a histogram using D3.js.
 *
 * @param {string} container_id - The ID of the HTML element where the chart will be appended.
 * @param {Array} array - The data array used to generate the histogram.
 * @param {number} input_width - The width of the histogram.
 * @returns {SVGElement} - The generated or updated SVG element containing the histogram.
 */
export function update_hist(container_id, array, input_width) {
    const svg = d3.select(`#${container_id} svg`);
    // Create
    if (svg.empty()) {
        return gen_hist(container_id, array, input_width);
    }
    // Update 
    else {
        svg.remove();
        return gen_hist(container_id, array, input_width);
    }
}